<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppMenuItem, { type MenuItemDefinition } from './AppMenuItem.vue'
import { MENU_DEFINITION } from './menuDefinition'
import { useMenuStore, type MenuNode } from '@/stores/menu'
import { useAuthStore } from '@/stores/auth'
import { haRuoloAmministratore } from '@/utils/ruoli'
import { useNovitaStore } from '@/modules/novita/stores/novita'
import { percorsoNormalizzato } from '@/modules/novita/lib/testiNovita'
// @ts-expect-error — layout composable is JS without type declarations
import { useLayout } from '@/layout/composables/layout'

const menuStore = useMenuStore()
const auth = useAuthStore()
const novitaStore = useNovitaStore()
const router = useRouter()
const { isHorizontal } = useLayout()
const { t, te } = useI18n()

function menuLabel(node: MenuNode): string {
  const key = `menu.${node.id}`
  return te(key) ? t(key) : node.label
}

const legacyBase = import.meta.env.VITE_LEGACY_BASE_URL ?? '/legacy'

function slug(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 30)
}

function groupPath(childRoutes: string[], fallbackLabel: string): string {
  const routes = childRoutes.filter(Boolean)
  if (!routes.length) return `/${slug(fallbackLabel)}`
  const firstParts = (routes[0] ?? '').split('/').filter(Boolean)
  for (let depth = firstParts.length; depth >= 1; depth--) {
    const prefix = '/' + firstParts.slice(0, depth).join('/')
    if (routes.every(r => r.startsWith(prefix))) return prefix
  }
  return `/${slug(fallbackLabel)}`
}

/**
 * True se la voce Vue porta a una pagina che l'utente può aprire, per le sole route con
 * `permissionAny`. Il server decide la voce da UN permesso mappato sulla funzione (OR fra i
 * mappati) e non sa dire «questo E uno fra questi»: senza il controllo la voce comparirebbe a
 * chi poi viene respinto dalla guardia del router (es. «Richieste del personale», funzione 1304:
 * READ più una fra VERIFICA, AUTORIZZA, ELIMINA). Stessa regola della guardia in router/index.ts.
 */
function routeApribile(url: string | null | undefined): boolean {
  if (!url) return true
  const meta = router.resolve(url).meta
  if (!meta.permissionAny?.length) {
    // Voce aperta anche all'amministratore dell'azienda (`oppureAmministratoreAzienda`): se il server l'ha mostrata per un
    // altro permesso mappato (es. AZIENDA_WRITE) ma l'utente non ha il permesso della route e non è amministratore, la
    // guardia lo rimanderebbe alla dashboard: meglio non mostrarla (stessa regola di router/index.ts).
    if (meta.permission && meta.oppureAmministratoreAzienda && !auth.has(meta.permission)) {
      return auth.isPlatformSuperAdmin || !!auth.me?.amministratoreGenerale
    }
    return true
  }
  const unoDeiPermessi = meta.permissionAny.some(p => auth.has(p))
    || (!!meta.oppureRuoloAmministratore && haRuoloAmministratore(auth.me?.roles))
  return unoDeiPermessi && (!meta.permission || auth.has(meta.permission))
}

function haNovita(url: string | null | undefined): boolean {
  const p = percorsoNormalizzato(url)
  return p != null && novitaStore.percorsiMenu.has(p)
}

function nodeToMenuItem(node: MenuNode): Record<string, unknown> | null {
  const item: Record<string, unknown> = {
    id: String(node.id),
    label: menuLabel(node),
    icon: node.icon,
  }
  // Voce passata di recente al portale nuovo: badge "Nuovo" per i primi giorni
  if (node.nuova) item.nuova = true
  // Funzione promossa da un annuncio «Novità» attivo per l'utente: stesso badge, collegato dal link dell'annuncio
  if (!node.isLegacy && !node.children?.length && haNovita(node.url)) item.nuova = true

  if (node.children && node.children.length > 0) {
    const childRoutes = node.children.filter(c => !c.isLegacy && c.url).map(c => c.url ?? '')
    item.path = groupPath(childRoutes, node.label)
    item.items = node.children
      .map(child => nodeToMenuItem(child))
      .filter((c): c is Record<string, unknown> => c !== null)
    if ((item.items as unknown[]).length === 0) return null
  } else if (node.isLegacy && node.url) {
    item.url = `${legacyBase}/home.seam?pagina=${encodeURIComponent(node.url)}`
    item.target = '_self'
  } else if (!node.isLegacy && node.url) {
    if (!routeApribile(node.url)) return null
    item.to = node.url
  }

  return item
}

function definitionToMenuItem(def: MenuItemDefinition): Record<string, unknown> | null {
  if (def.permission && !auth.has(def.permission)) return null
  if (def.superAdminOnly && !auth.isPlatformSuperAdmin) return null
  if (def.requiresAziendaLotti && !auth.me?.abilitaLotti) return null

  const item: Record<string, unknown> = { label: def.label, icon: def.icon }

  if (def.children !== undefined) {
    const visible = def.children
      .map(definitionToMenuItem)
      .filter((c): c is Record<string, unknown> => c !== null)
    if (visible.length === 0) return null
    const childRoutes = def.children?.map(c => c.to).filter((t): t is string => !!t) ?? []
    item.path = groupPath(childRoutes, def.label)
    item.items = visible
  } else if (def.legacyUrl) {
    item.url = `${legacyBase}/home.seam?pagina=${encodeURIComponent(def.legacyUrl)}`
    item.target = '_self'
  } else if (def.to) {
    item.to = def.to
    if (haNovita(def.to)) item.nuova = true
  }

  return item
}

// Funzione del menu "Assistenza/Helpdesk" (funzioni_2, riservata Vue ≥9000): va SEMPRE
// in fondo al menu, a prescindere dall'ordine in funzioni_2.
const ASSISTENZA_FUNZIONE_ID = 9002

const model = computed<Array<Record<string, unknown>>>(() => {
  const items = menuStore.loaded && menuStore.items.length > 0
    ? [...menuStore.items]
        .sort((a, b) =>
          (a.id === ASSISTENZA_FUNZIONE_ID ? 1 : 0) - (b.id === ASSISTENZA_FUNZIONE_ID ? 1 : 0),
        )
        .map(node => nodeToMenuItem(node))
        .filter((i): i is Record<string, unknown> => i !== null)
    : MENU_DEFINITION.map(definitionToMenuItem).filter((i): i is Record<string, unknown> => i !== null)

  if ((isHorizontal as { value: boolean }).value) {
    return items
  }
  return [{ label: 'Menu', items }]
})
</script>

<template>
  <ul class="layout-menu">
    <template
      v-for="(item, index) in model"
      :key="(item['label'] as string) + '_' + index"
    >
      <AppMenuItem
        v-if="!item['separator']"
        :item="item"
        :index="index"
        root
      />
      <li
        v-else
        class="menu-separator"
      />
    </template>
  </ul>
</template>
